import { createContext, useContext, useEffect, useState } from 'react';
import styles from './Tabs.module.css';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs subcomponents must be used inside <Tabs>');
  return ctx;
}

// ─── Tabs root ────────────────────────────────────────────────────────────────

interface TabsProps {
  defaultTab: string;
  /** Controlled: the active tab follows this value (a page keeps it in `?tab=`). */
  activeTab?: string;
  onChange?: (tab: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function Tabs({ defaultTab, activeTab: controlled, onChange, children, className }: TabsProps) {
  const [internal, setInternal] = useState(controlled ?? defaultTab);
  useEffect(() => {
    if (controlled !== undefined) setInternal(controlled);
  }, [controlled]);
  const activeTab = controlled ?? internal;

  function handleSet(id: string) {
    setInternal(id);
    onChange?.(id);
  }

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleSet }}>
      <div className={[styles.root, className].filter(Boolean).join(' ')}>{children}</div>
    </TabsContext.Provider>
  );
}

// ─── Tab list ─────────────────────────────────────────────────────────────────

interface TabListProps {
  children: React.ReactNode;
  className?: string;
}

Tabs.List = function TabList({ children, className }: TabListProps) {
  return (
    <div className={[styles.list, className].filter(Boolean).join(' ')} role="tablist">
      {children}
    </div>
  );
};

// ─── Individual tab ───────────────────────────────────────────────────────────

interface TabProps {
  id: string;
  children: React.ReactNode;
  icon?: string;
  badge?: string | number;
}

Tabs.Tab = function Tab({ id, children, icon, badge }: TabProps) {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === id;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      aria-controls={`tabpanel-${id}`}
      id={`tab-${id}`}
      className={[styles.tab, isActive && styles.active].filter(Boolean).join(' ')}
      onClick={() => setActiveTab(id)}
    >
      {icon && <i className={`fa-solid fa-${icon}`} aria-hidden="true" />}
      {children}
      {badge !== undefined && <span className={styles.badge}>{badge}</span>}
    </button>
  );
};

// ─── Tab panels ───────────────────────────────────────────────────────────────

interface TabPanelProps {
  id: string;
  children: React.ReactNode;
  className?: string;
}

Tabs.Panel = function TabPanel({ id, children, className }: TabPanelProps) {
  const { activeTab } = useTabsContext();
  if (activeTab !== id) return null;

  return (
    <div
      id={`tabpanel-${id}`}
      role="tabpanel"
      aria-labelledby={`tab-${id}`}
      className={[styles.panel, className].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
};
